import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
    ApiFlavor,
    type ByoLlmConfiguration,
    ByoLlmConfigurationType,
    type ByoLlmModelConfiguration,
    type ByoOperationGroupConfiguration,
    type ByoProductLlmConfiguration,
    type ByoProductLlmConfigurationWithFormattedFields,
    ConfigurationStatusTranslationKeys,
    CustomConnectorTagToTemplate,
    type CustomConnectorTemplate,
    IntegrationServiceConnectorTypesToLlmConnectorTypes,
    KnownProductFeatureNames,
    KnownRequestingProducts,
    LlmConfigurationStatus,
    LlmConnectorType,
    LlmConnectorTypesToApiFlavors,
    LlmConnectorTypesToIntegrationServiceConnectorTypes,
    LlmConnectorTypesToModelFamily,
    ModelFamily,
    ModelsConfigurationOption,
} from './LlmGateway.types';

// Derived from LlmConnectorTypesToApiFlavors - maps API flavor to connector types that support it
export const ApiFlavorsToLlmConnectorTypes: { [key in ApiFlavor]: LlmConnectorType[] } = Object.entries(
    LlmConnectorTypesToApiFlavors
).reduce(
    (acc, [ connectorType, apiFlavors ]) => {
        apiFlavors.forEach((flavor) => {
            if (!acc[flavor]) {
                acc[flavor] = [];
            }
            acc[flavor].push(connectorType as LlmConnectorType);
        });
        return acc;
    },
    {} as { [key in ApiFlavor]: LlmConnectorType[] }
);

export function useFilteredProductConfigurations(productConfigurations?: Record<string, Record<string, ByoOperationGroupConfiguration>>) {
    const isIxpEnabled = useFeatureFlagValue(Features.EnableAiTrustLayerByoLlmIXP.name);

    return useMemo(() => {
        if (!productConfigurations) {
            return undefined;
        }

        const filteredConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>> = {};

        Object.entries(productConfigurations).forEach(([ product, features ]) => {
            if (product.toUpperCase() === KnownRequestingProducts.IXP && !isIxpEnabled) {
                return; // Skip IXP if it's not enabled in BYO LLM
            }

            filteredConfigurations[product] = structuredClone(features); // Clone to avoid mutation
        });

        return filteredConfigurations;
    }, [ productConfigurations, isIxpEnabled ]);
}

export function getAvailableLlms(
    productLlmConfigurations: ByoProductLlmConfiguration[],
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>>,
    canBypassConfigurationUniqueness: boolean
): Record<string, Record<string, string[]>> {
    // Compute list of LLMs for each product and operation group
    const baseConfigurations = Object.entries(productConfigurations).reduce((prodAcc, [ product, operationGroups ]) => {
        prodAcc[product] = Object.entries(operationGroups).reduce((opAcc, [ operationGroupName, operationGroup ]) => {
            opAcc[operationGroupName] = [ ...operationGroup.models.map(llm => llm.model) ];
            return opAcc;
        }, {} as Record<string, string[]>);
        return prodAcc;
    }, {} as Record<string, Record<string, string[]>>);

    if (canBypassConfigurationUniqueness) {
        // If the uniqueness constraint is bypassed, return all available LLMs
        return baseConfigurations;
    }

    // Compute list of already configured LLMs for each product
    const configuredLlms = getConfiguredLlms(productLlmConfigurations);

    if (!configuredLlms) {
        return baseConfigurations;
    }

    // Compute difference between the two lists
    return Object.entries(baseConfigurations)
        .reduce((prodAcc, [ product, operationGroups ]) => {
            prodAcc[product] = Object.entries(operationGroups).reduce((opAcc, [ operationGroupName, operationGroup ]) => {
                if (productConfigurations[product]?.[operationGroupName]?.modelsConfigurationOption === ModelsConfigurationOption.AllModels
                    && operationGroupName in (configuredLlms[product] ?? {})) {
                    return opAcc; // Only a single operation is allowed for "All Models" configuration, so if there's already a configured LLM for this operation group
                }

                opAcc[operationGroupName] = operationGroup.filter(llm => !configuredLlms[product]?.[operationGroupName]?.includes(llm));
                if (opAcc[operationGroupName].length === 0) {
                    delete opAcc[operationGroupName];
                }
                return opAcc;
            }, {} as Record<string, string[]>);

            if (Object.keys(prodAcc[product]).length === 0) {
                delete prodAcc[product];
            }

            return prodAcc;
        }, {} as Record<string, Record<string, string[]>>);
}

export function getConfiguredLlms(configurations: ByoProductLlmConfiguration[]): Record<string, Record<string, string[]>> {
    // Compute list of already configured LLMs for each product and operation group
    return configurations.reduce((acc, config) => {
        if (!acc[config.product]) {
            acc[config.product] = {} as Record<string, string[]>;
        }

        if (!acc[config.product][config.operationGroupName]) {
            acc[config.product][config.operationGroupName] = [];
        }

        acc[config.product][config.operationGroupName].push(
            ...config.llmConfigurations.map(llmConfig => llmConfig.desiredModel)
        );
        return acc;
    }, {} as Record<string, Record<string, string[]>>);
}

export function useFormattedProductLlmConfigurations(configurations: ByoProductLlmConfiguration[] | undefined): ByoProductLlmConfigurationWithFormattedFields[] {
    const { formatMessage: translate } = useIntl();

    // GA flags
    const isScreenPlayGaEnabled = useFeatureFlagValue(Features.AiTrustLayerByoLlmScreenPlayGa.name);
    const isSemanticTargetingGaEnabled = useFeatureFlagValue(Features.AiTrustLayerByoLlmSemanticTargetingGa.name);

    // V2 flag
    const isV2Enabled = useFeatureFlagValue(Features.EnableAiTrustLayerByoLlmConfigurationsV2.name);

    return useMemo(() => configurations?.map(config => {
        const productIntl = translate({ id: getProductTranslationKey(config.product) });
        let operationGroupNameIntl = translate({ id: getProductFeatureTranslationKey(config.operationGroupName) });

        if ((config.operationGroupName.toUpperCase() === KnownProductFeatureNames.UiaScreenPlay && isScreenPlayGaEnabled) ||
            (config.operationGroupName.toUpperCase() === KnownProductFeatureNames.UiaSemanticTargeting && isSemanticTargetingGaEnabled)) {
            operationGroupNameIntl = translate({ id: getProductFeatureGaTranslationKey(config.operationGroupName) });
        }

        const modelsList = config.llmConfigurations.map(llmConfig => llmConfig.desiredModel).join(', ');
        let aggregatedConnectionStatus: LlmConfigurationStatus;

        if (config.enabled) {
            if (isV2Enabled && config.llmConfigurations.some(llmConfig => llmConfig.configurationType !== ByoLlmConfigurationType.SelfServe)) {
                aggregatedConnectionStatus = LlmConfigurationStatus.UpgradeNeeded;
            } else if (config.llmConfigurations.every(llmConfig => llmConfig.validConnection)) {
                aggregatedConnectionStatus = LlmConfigurationStatus.Connected;
            } else {
                aggregatedConnectionStatus = LlmConfigurationStatus.Failed;
            }
        } else {
            aggregatedConnectionStatus = LlmConfigurationStatus.Disabled;
        }

        const aggregatedConnectionStatusIntl = translate({ id: ConfigurationStatusTranslationKeys[aggregatedConnectionStatus] });

        return {
            ...config,
            productIntl,
            operationGroupNameIntl,
            modelsList,
            status: aggregatedConnectionStatus,
            statusIntl: aggregatedConnectionStatusIntl,
        };
    }) ?? [], [ configurations, isScreenPlayGaEnabled, isSemanticTargetingGaEnabled, translate ]);
}

export const getProductTranslationKey = (product: string) => `CLIENT_BYO_LLM_ONBOARDED_PRODUCT_${product.toUpperCase()}_LABEL`;

export const getAvailableDialectsForModel = (model: string, modelToConnectorTypeMapping: Record<string, LlmConnectorType[]>): ModelFamily[] => {
    const connectorType = modelToConnectorTypeMapping[model][0];
    const modelFamily = LlmConnectorTypesToModelFamily[connectorType];
    return Array.from(new Set([ modelFamily, ModelFamily.OpenAi ]));
};

export const getProductFeatureTranslationKey = (operationGroupName: string) =>
    `CLIENT_BYO_LLM_ONBOARDED_PRODUCT_FEATURE_${operationGroupName.toUpperCase()}_LABEL`;

export const getProductFeatureGaTranslationKey = (operationGroupName: string) =>
    `CLIENT_BYO_LLM_ONBOARDED_PRODUCT_FEATURE_${operationGroupName.toUpperCase()}_GA_LABEL`;

export function getModelIndex(
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>> | undefined,
    product: string,
    feature: string,
    modelName: string
): number {
    if (!productConfigurations?.[product]?.[feature] || !modelName) {
        return -1;
    }
    return productConfigurations[product][feature].models.findIndex(m => m.model === modelName);
}

export const getFeatureDescriptionTranslationKey = (product: string, feature: string): string =>
    `CLIENT_BYO_LLM_FEATURE_${product.toUpperCase()}_${feature.toUpperCase()}_DESCRIPTION`;

export const getModelTitleTranslationKey = (product: string, feature: string, modelIndex: number): string =>
    `CLIENT_BYO_LLM_MODEL_${product.toUpperCase()}_${feature.toUpperCase()}_${modelIndex}_TITLE`;

export const getModelDescriptionTranslationKey = (product: string, feature: string, modelIndex: number): string =>
    `CLIENT_BYO_LLM_MODEL_${product.toUpperCase()}_${feature.toUpperCase()}_${modelIndex}_DESCRIPTION`;

export const translationKeyExists = (
    translate: (descriptor: { id: string }) => string,
    key: string
): boolean => translate({ id: key }) !== key;

export const isJarvisNaturalLanguageQueryFeature = (product: string, feature: string): boolean => (
    product === 'jarvis' && feature === 'jarvis-natural-language-query'
);

const getJarvisModelLabelId = (baseModelName?: string): string | undefined => {
    if (!baseModelName) {
        return undefined;
    }
    const lower = baseModelName.toLowerCase();
    if (lower.includes('gpt-4o-mini') || lower.includes('gpt-4.1-mini-2025-04-14')) {
        return 'CLIENT_BYO_LLM_FAST_MODEL';
    }
    if (lower.includes('claude-3-7-sonnet') || lower.includes('anthropic.claude-sonnet-4-5-20250929-v1:0')) {
        return 'CLIENT_BYO_LLM_SLOW_MODEL';
    }
    return undefined;
};

const getJarvisModelTooltipId = (baseModelName?: string): string | undefined => {
    if (!baseModelName) {
        return undefined;
    }
    const lower = baseModelName.toLowerCase();
    if (lower.includes('gpt-4o-mini') || lower.includes('gpt-4.1-mini-2025-04-14')) {
        return 'CLIENT_BYO_LLM_FAST_MODEL_TOOLTIP';
    }
    if (lower.includes('claude-3-7-sonnet') || lower.includes('anthropic.claude-sonnet-4-5-20250929-v1:0')) {
        return 'CLIENT_BYO_LLM_SLOW_MODEL_TOOLTIP';
    }
    return undefined;
};

export const getAlternativeLabelTranslationId = (
    product: string,
    feature: string,
    baseModelName?: string,
): string => {
    if (isJarvisNaturalLanguageQueryFeature(product, feature)) {
        const byName = getJarvisModelLabelId(baseModelName);
        if (byName) {
            return byName;
        }
    }
    return 'CLIENT_BYO_LLM_REPLACED_MODEL';
};

export const getAlternativeTooltipTranslationId = (
    product: string,
    feature: string,
    baseModelName?: string,
): string | undefined => {
    if (isJarvisNaturalLanguageQueryFeature(product, feature)) {
        const tooltipId = getJarvisModelTooltipId(baseModelName);
        if (tooltipId) {
            return tooltipId;
        }
    }
    return undefined;
};

export const getLlmConnectorTypeIntegrationServiceEquivalent = (connectorType?: LlmConnectorType): string | undefined => {
    if (!connectorType) {
        return undefined;
    }

    return LlmConnectorTypesToIntegrationServiceConnectorTypes[connectorType] || undefined;
};

export const getConnectorDropdownValue = (llmConfiguration: Partial<ByoLlmConfiguration>): string | undefined =>
    getLlmConnectorTypeIntegrationServiceEquivalent(llmConfiguration.connectorType)
    ?? llmConfiguration.selfServeByoConfigParameters?.connectorTemplate;

export const handleConnectorChange = (
    connectorKey: string | undefined,
    llmConfiguration: Partial<ByoLlmConfiguration>,
    connectorTags?: string,
) => {
    const mappedConnectorType = IntegrationServiceConnectorTypesToLlmConnectorTypes[connectorKey ?? ''];
    const isCustomConnector = !mappedConnectorType;
    llmConfiguration.connectorType = mappedConnectorType ?? LlmConnectorType.Custom;
    if (isCustomConnector && connectorKey) {
        llmConfiguration.selfServeByoConfigParameters = { connectorTemplate: connectorKey };
    } else {
        llmConfiguration.selfServeByoConfigParameters = undefined;
    }
    llmConfiguration.connectorTags = connectorTags;
    llmConfiguration.connectionId = '';
    llmConfiguration.customerModel = '';
};

export const getLlmProductTranslationKey = (product: string) => {
    if (product.toUpperCase() === KnownRequestingProducts.UiAgents) {
        // Special case for UIA product until revamp
        return 'CLIENT_BYO_LLM_ONBOARDED_PRODUCT_UIA_AGENT_LABEL';
    }
    return `CLIENT_BYO_LLM_ONBOARDED_PRODUCT_${product.toUpperCase()}_LABEL`;
};

export const getLlmIncompatibilityReasonTranslationKey = (incompatibilityReason: string): string => `CLIENT_BYO_LLM_INCOMPATIBILITY_${incompatibilityReason.toUpperCase()}_TEXT`;

export const getLlmUnavailabilityReasonTranslationKey = (unavailabilityReason: string): string => `CLIENT_BYO_LLM_UNAVAILABILITY_${unavailabilityReason.toUpperCase()}_TEXT`;

export const isAiGatewayCustomAuthConnector = (connectorKey: string): boolean => connectorKey.startsWith('custom-') && connectorKey.includes('customllmauth');

export function getModelConfiguration(
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>> | undefined,
    targetProduct: string,
    targetFeature: string,
    targetModel: string
): ByoLlmModelConfiguration | undefined {
    if (!productConfigurations || !targetProduct || !targetFeature || !targetModel) {
        return undefined;
    }

    const productOperationGroups = productConfigurations[targetProduct];
    if (!productOperationGroups) {
        return undefined;
    }

    const featureConfiguration = productOperationGroups[targetFeature];
    if (!featureConfiguration) {
        return undefined;
    }

    return featureConfiguration.models.find(
        (config: ByoLlmModelConfiguration) => config.model === targetModel
    );
}

export function hasModelAlternatives(
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>> | undefined,
    targetProduct: string,
    targetFeature: string,
    targetModel: string
): boolean {
    const modelConfig = getModelConfiguration(productConfigurations, targetProduct, targetFeature, targetModel);
    return !!(
        (modelConfig?.alternatives && modelConfig.alternatives.length > 0) ||
        (modelConfig?.alternativesWithProbes && modelConfig.alternativesWithProbes.length > 0)
    );
}

export function getCurrentSelectedAlternative(llmConfig: Partial<ByoLlmConfiguration>): string | undefined {
    if (llmConfig.defaultModel && llmConfig.desiredModel &&
        llmConfig.defaultModel !== llmConfig.desiredModel) {
        return llmConfig.desiredModel;
    }
    return undefined;
}

export function createEmptyLlmConfiguration(
    desiredModel: string,
    useAiGateway: boolean,
    setDefaultModel: boolean = false,
    isCustomAlias?: boolean,
): Partial<ByoLlmConfiguration> {
    const config: Partial<ByoLlmConfiguration> = {
        desiredModel,
        isCustomAlias,
    };

    if (setDefaultModel) {
        config.defaultModel = desiredModel;
    }

    if (useAiGateway) {
        config.gatewayParameters = {
            apiUrl: '',
            authConnectionId: '',
            authConnectorType: '',
            vendorApiType: ModelFamily.OpenAi,
        };
    }

    return config;
}

export function createConfigurationWithAlternative(
    llmConfiguration: Partial<ByoLlmConfiguration>,
    selectedAlternative: string,
    originalDesiredModel: string,
    useAiGateway: boolean,
    isCustomAlias?: boolean
): Partial<ByoLlmConfiguration> {
    const updatedConfiguration: Partial<ByoLlmConfiguration> = {
        ...llmConfiguration,
        defaultModel: llmConfiguration.defaultModel ?? llmConfiguration.desiredModel ?? originalDesiredModel,
        desiredModel: selectedAlternative,
        isCustomAlias,
        connectorType: undefined,
        connectionId: useAiGateway ? undefined : '',
        customerModel: '',
    };

    if (useAiGateway) {
        updatedConfiguration.gatewayParameters = {
            apiUrl: '',
            authConnectionId: '',
            authConnectorType: '',
            vendorApiType: ModelFamily.OpenAi,
        };
    }

    return updatedConfiguration;
}

export function getApiFlavorsForModel(
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>> | undefined,
    product: string,
    feature: string,
    modelName: string | undefined,
    isAddYourOwnModel: boolean
): ApiFlavor[] {
    if (!productConfigurations || !product || !feature) {
        return [];
    }

    const operationGroupConfig = productConfigurations[product]?.[feature];
    if (!operationGroupConfig) {
        return [];
    }

    // Handle "Add Your Own Model" case
    if (isAddYourOwnModel && operationGroupConfig.probes) {
        const flavors = operationGroupConfig.probes
            .map(probe => probe.apiFlavor)
            .filter((flavor): flavor is keyof typeof ApiFlavor => flavor in ApiFlavor) as ApiFlavor[];
        return Array.from(new Set(flavors));
    }

    const modelConfig = operationGroupConfig.models.find(m => m.model === modelName);
    if (!modelConfig?.probes) {
        return [];
    }

    const apiFlavorsWithDuplicates = modelConfig.probes
        .map(probe => probe.apiFlavor)
        .filter((flavor): flavor is keyof typeof ApiFlavor => flavor in ApiFlavor) as ApiFlavor[];

    return Array.from(new Set(apiFlavorsWithDuplicates));
}

export function getApiFlavorsForAlternative(
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>> | undefined,
    product: string,
    feature: string,
    baseModelName: string,
    alternativeModelName: string
): ApiFlavor[] {
    if (!productConfigurations || !product || !feature) {
        return [];
    }

    const operationGroupConfig = productConfigurations[product]?.[feature];
    if (!operationGroupConfig) {
        return [];
    }

    const modelConfig = operationGroupConfig.models.find(m => m.model === baseModelName);
    if (!modelConfig?.alternativesWithProbes) {
        return [];
    }

    const alternative = modelConfig.alternativesWithProbes.find(alt => alt.model === alternativeModelName);
    if (!alternative?.probes) {
        return [];
    }

    const apiFlavorsWithDuplicates = alternative.probes
        .map(probe => probe.apiFlavor)
        .filter((flavor): flavor is keyof typeof ApiFlavor => flavor in ApiFlavor) as ApiFlavor[];

    return Array.from(new Set(apiFlavorsWithDuplicates));
}

export function getDefaultAvailableApiFlavors(
    vendorLlmApiFlavors: Record<string, ApiFlavor[] | null> | undefined,
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>> | undefined,
    product: string,
    feature: string,
    currentModel: string | undefined,
): ApiFlavor[] | undefined {
    const featureProbes = productConfigurations?.[product]?.[feature]?.probes;
    const featureProbeFlavors = featureProbes?.length
        ? new Set(featureProbes.map(p => p.apiFlavor).filter((f): f is ApiFlavor => f in ApiFlavor))
        : undefined;

    if (!currentModel) {
        return featureProbeFlavors ? Array.from(featureProbeFlavors) : Object.values(ApiFlavor);
    }

    const supportedFlavors = vendorLlmApiFlavors?.[currentModel];
    if (!supportedFlavors) {
        // Model not in vendor map (generic alternative or custom model) — let probes decide
        return undefined;
    }

    const defaultFlavors = new Set<ApiFlavor>(supportedFlavors);
    defaultFlavors.add(ApiFlavor.OpenAiChatCompletions);

    if (featureProbeFlavors) {
        return Array.from(defaultFlavors).filter(flavor => featureProbeFlavors.has(flavor));
    }

    return Array.from(defaultFlavors);
}

export interface DeprecatedModelInfo {
    model: string;
    usageEndDate: string;
}

export function getDeprecatedModelsFromConfig(modelConfig: ByoLlmModelConfiguration | undefined): Map<string, DeprecatedModelInfo> {
    const deprecated = new Map<string, DeprecatedModelInfo>();

    if (!modelConfig) {
        return deprecated;
    }

    if (modelConfig.usageEndDate && modelConfig.usageEndDate !== 'N/A') {
        deprecated.set(modelConfig.model, {
            model: modelConfig.model,
            usageEndDate: modelConfig.usageEndDate,
        });
    }

    if (modelConfig.alternativesWithProbes) {
        modelConfig.alternativesWithProbes.forEach(alt => {
            if (alt.usageEndDate && alt.usageEndDate !== 'N/A') {
                deprecated.set(alt.model, {
                    model: alt.model,
                    usageEndDate: alt.usageEndDate,
                });
            }
        });
    }

    return deprecated;
}

export function getDeprecatedModelsWithInfo(
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>> | undefined,
    product: string,
    feature: string
): Map<string, DeprecatedModelInfo> {
    const deprecated = new Map<string, DeprecatedModelInfo>();

    if (!productConfigurations || !product || !feature) {
        return deprecated;
    }

    const operationGroupConfig = productConfigurations[product]?.[feature];
    if (!operationGroupConfig) {
        return deprecated;
    }

    operationGroupConfig.models.forEach(modelConfig => {
        const deprecatedInModel = getDeprecatedModelsFromConfig(modelConfig);
        deprecatedInModel.forEach((info, modelName) => {
            deprecated.set(modelName, info);
        });
    });

    return deprecated;
}

export function getDeprecatedModels(
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>> | undefined,
    product: string,
    feature: string
): Set<string> {
    const deprecatedWithInfo = getDeprecatedModelsWithInfo(productConfigurations, product, feature);
    const deprecated = new Set<string>();

    deprecatedWithInfo.forEach((_info, modelName) => {
        deprecated.add(modelName);
    });

    return deprecated;
}

export function getConnectorTemplateFromTags(tags: string | undefined): CustomConnectorTemplate | undefined {
    if (!tags) {
        return undefined;
    }

    for (const [ tag, template ] of Object.entries(CustomConnectorTagToTemplate)) {
        if (tags.includes(tag)) {
            return template;
        }
    }

    return undefined;
}

export const checkIsFormComplete = (llmConfigurations: any): boolean => {
    if (!llmConfigurations || Object.keys(llmConfigurations).length === 0) {
        return false;
    }

    for (const llmConfig of Object.values(llmConfigurations)) {
        const config = llmConfig as Partial<ByoLlmConfiguration> | undefined;
        if (!config?.desiredModel || !config?.customerModel || !config?.connectionId || !config?.connectorType || !config?.apiFlavor) {
            return false;
        }
    }

    return true;
};
